/**
 * Step 3 — one provider check: what it covered, the registry result, the
 * declared-vs-registry comparison, candidates to choose from, screening hits
 * and the moderator's decision.
 *
 * Any moderator refreshes a running check and picks a candidate; super
 * moderators decide hits and confirm / dismiss (backend `super_actions`).
 * Confirming records `approved` only when the registry matched and no hit was
 * confirmed, otherwise `rejected` — the preview says which before the click.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EMPTY_VALUE, formatCurrency, formatDate } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import {
  CHECK_STATUS_CHIP,
  checkDecisionKey,
  checkStatusKey,
  confirmOutcome,
  DECISION_CHIP,
  isCheckInFlight,
  isUseCase,
  MATCH_CHIP,
} from "@/lib/kyb";
import type {
  VerificationCheck,
  VerificationComparison,
  VerificationFieldEnum,
  VerificationHit,
  VerificationRegistryPerson,
  VerificationRegistryResult,
} from "@/services/moderators/generated/model";
import {
  useVerificationModeratorChecksConfirmCreate,
  useVerificationModeratorChecksDismissCreate,
  useVerificationModeratorChecksRefreshCreate,
  useVerificationModeratorChecksSelectCandidateCreate,
  useVerificationModeratorHitsDecideCreate,
} from "@/services/moderators/generated/moderators-verification/moderators-verification";

export const KybCheckCard = ({
  check,
  isSuper,
  onChanged,
}: {
  check: VerificationCheck;
  isSuper: boolean;
  onChanged: () => void;
}) => {
  const { t } = useTranslation(["kyb", "common"]);
  const status = checkStatusKey(check.status);
  const decision = checkDecisionKey(check.decision);
  const refresh = useVerificationModeratorChecksRefreshCreate({ mutation: { onSuccess: onChanged } });
  const registry = check.registryResult as VerificationRegistryResult | null;
  const comparison = check.comparison as VerificationComparison | null;

  return (
    <article id={`check-${check.id}`} className="kyb-section">
      <h3 className="kyb-section__title">
        {check.kind === "monitoring"
          ? t("check.titleMonitoring", { id: check.id, provider: check.provider })
          : t("check.titleKyb", { id: check.id, provider: check.provider })}
        <span className={`mod-page__chip ${CHECK_STATUS_CHIP[status]}`}>{t(`check.status.${status}`)}</span>
        <span className={`mod-page__chip ${DECISION_CHIP[decision]}`}>{t(`check.decision.${decision}`)}</span>
        {!check.badgeEligible ? (
          <span className="mod-page__chip mod-page__chip--warning">{t("check.notBadgeEligible")}</span>
        ) : null}
      </h3>
      <p className="mod-page__card-meta">
        {t("check.created", { date: formatDate(check.createdAt) })}
        {check.requestedBy ? ` · ${t("check.requestedBy", { name: check.requestedBy })}` : ""}
        {check.completedAt ? ` · ${t("check.completed", { date: formatDate(check.completedAt) })}` : ""}
        {check.cost ? ` · ${t("check.cost", { cost: formatCurrency(check.cost, check.costCurrency || "USD") })}` : ""}
        {check.vendorReference ? ` · ${t("check.vendorRef", { ref: check.vendorReference })}` : ""}
      </p>

      <div className="kyb-provider__tags">
        <span className="kyb-note">{t("check.covered")}</span>
        {check.covered.length === 0 ? <span className="kyb-note">{EMPTY_VALUE}</span> : null}
        {check.covered.map((c) => (
          <span key={c} className="mod-page__chip mod-page__chip--success">
            {isUseCase(c) ? t(`useCase.${c}`) : c}
          </span>
        ))}
      </div>

      {isCheckInFlight(check) ? (
        <div className="kyb-callout">
          {t("check.inFlight")}{" "}
          <button
            type="button"
            className="mod-page__btn mod-page__btn--ghost"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate({ id: check.id })}
          >
            {refresh.isPending ? t("check.actions.refreshing") : t("check.actions.refresh")}
          </button>
        </div>
      ) : null}
      {refresh.isError ? (
        <div className="mod-page__inline-error">{getApiErrorMessage(refresh.error, t("check.refreshError"))}</div>
      ) : null}
      {check.error ? <div className="kyb-callout kyb-callout--danger">{check.error}</div> : null}

      {status === "needs_selection" ? <CandidatePicker check={check} onChanged={onChanged} /> : null}

      {registry ? <RegistryBlock registry={registry} selected={check.selectedCandidate} /> : null}
      {comparison ? <ComparisonBlock comparison={comparison} /> : null}

      {check.covered.some((c) => c === "aml_company" || c === "aml_people") || check.hits.length > 0 ? (
        <div className="kyb-sub">
          <h4 className="kyb-sub__title">{t("check.hitsTitle", { count: check.hits.length })}</h4>
          {check.hits.length === 0 ? <p className="kyb-note">{t("check.noHits")}</p> : null}
          {check.hits.map((hit) => (
            <HitRow
              key={hit.id}
              hit={hit}
              canAct={isSuper && decision === "pending" && hit.decision === "pending"}
              onDecided={onChanged}
            />
          ))}
        </div>
      ) : null}

      <ProposalBlock check={check} />

      {check.decidedBy && check.decidedAt ? (
        <p className="mod-page__card-meta kyb-sub">
          {t("check.decidedBy", { name: check.decidedBy, date: formatDate(check.decidedAt) })}
          {check.decisionReason ? ` — ${check.decisionReason}` : ""}
          {check.appliedFields.length > 0
            ? ` · ${t("check.appliedFields", { count: check.appliedFields.length })}`
            : ""}
        </p>
      ) : null}

      {decision === "pending" && !isCheckInFlight(check) && status !== "needs_selection" ? (
        <DecisionBlock check={check} isSuper={isSuper} onChanged={onChanged} />
      ) : null}
      {decision === "pending" && status === "needs_selection" && isSuper ? (
        <DecisionBlock check={check} isSuper={isSuper} onChanged={onChanged} dismissOnly />
      ) : null}
    </article>
  );
};

// ------------------------------------------------------------------ Candidates

const CandidatePicker = ({ check, onChanged }: { check: VerificationCheck; onChanged: () => void }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const select = useVerificationModeratorChecksSelectCandidateCreate({ mutation: { onSuccess: onChanged } });
  return (
    <div className="kyb-sub">
      <h4 className="kyb-sub__title">{t("check.candidates.title", { count: check.candidates.length })}</h4>
      <p className="kyb-note">{t("check.candidates.help")}</p>
      <div className="kyb-scroll">
        <table className="kyb-table">
          <thead>
            <tr>
              <th>{t("check.candidates.name")}</th>
              <th>{t("check.candidates.number")}</th>
              <th>{t("check.candidates.status")}</th>
              <th>{t("check.candidates.registered")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {check.candidates.map((c) => (
              <tr key={c.candidateId}>
                <td>
                  <strong>{c.name}</strong>
                  <div className="kyb-note">
                    {c.entityType} · {c.country}
                  </div>
                </td>
                <td>{c.registrationNumber || EMPTY_VALUE}</td>
                <td>{c.entityStatus || EMPTY_VALUE}</td>
                <td>{c.registrationDate ? formatDate(c.registrationDate) : EMPTY_VALUE}</td>
                <td>
                  <button
                    type="button"
                    className="mod-page__btn mod-page__btn--primary"
                    disabled={select.isPending}
                    onClick={() => select.mutate({ id: check.id, data: { candidateId: c.candidateId } })}
                  >
                    {t("check.candidates.choose")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="kyb-note">{t("check.candidates.noneOfThese")}</p>
      {select.isError ? (
        <div className="mod-page__inline-error">{getApiErrorMessage(select.error, t("check.candidates.error"))}</div>
      ) : null}
    </div>
  );
};

// --------------------------------------------------------------------- Registry

const RegistryBlock = ({ registry, selected }: { registry: VerificationRegistryResult; selected: string | null }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const people: (VerificationRegistryPerson & { source: "officer" | "ubo" })[] = [
    ...(registry.officers ?? []).map((p) => ({ ...p, source: "officer" as const })),
    ...(registry.ubos ?? []).map((p) => ({ ...p, source: "ubo" as const })),
  ];
  return (
    <div className="kyb-sub">
      <h4 className="kyb-sub__title">
        {t("check.registry.title")}{" "}
        {registry.status ? (
          <span
            className={`mod-page__chip ${registry.status === "matched" ? "mod-page__chip--success" : "mod-page__chip--warning"}`}
          >
            {registry.status}
          </span>
        ) : null}
      </h4>
      <dl className="kyb-form__grid kyb-note">
        <div>
          <dt>{t("check.registry.registeredName")}</dt>
          <dd>{registry.registeredName || EMPTY_VALUE}</dd>
        </div>
        <div>
          <dt>{t("check.registry.number")}</dt>
          <dd>{registry.registrationNumber || EMPTY_VALUE}</dd>
        </div>
        <div>
          <dt>{t("check.registry.registry")}</dt>
          <dd>
            {registry.sourceUrl ? (
              <a href={registry.sourceUrl} target="_blank" rel="noreferrer">
                {registry.registryName || registry.sourceUrl}
              </a>
            ) : (
              registry.registryName || EMPTY_VALUE
            )}
          </dd>
        </div>
        <div>
          <dt>{t("check.registry.entityStatus")}</dt>
          <dd>
            {registry.entityStatus || EMPTY_VALUE}
            {registry.jurisdiction ? ` · ${registry.jurisdiction}` : ""}
          </dd>
        </div>
        {selected ? (
          <div>
            <dt>{t("check.registry.selected")}</dt>
            <dd>{selected}</dd>
          </div>
        ) : null}
      </dl>
      {people.length > 0 ? (
        <div className="kyb-scroll">
          <table className="kyb-table">
            <thead>
              <tr>
                <th>{t("check.registry.person")}</th>
                <th>{t("check.registry.role")}</th>
                <th>{t("check.registry.ownership")}</th>
                <th>{t("check.registry.active")}</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={`${p.source}-${p.name}-${p.role}`}>
                  <td>
                    {p.name}
                    {p.nationality ? <div className="kyb-note">{p.nationality}</div> : null}
                  </td>
                  <td>
                    <span className="mod-page__chip">{t(`check.registry.source.${p.source}`)}</span>
                    {p.role}
                  </td>
                  <td>{p.ownership || EMPTY_VALUE}</td>
                  <td>{p.active ? t("common:labels.yes") : t("common:labels.no")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

// ------------------------------------------------------------------ Comparison

const matchKey = (m: string) =>
  (["same", "similar", "different", "unknown", "not_in_registry"].includes(m) ? m : "unknown") as
    | "same"
    | "similar"
    | "different"
    | "unknown"
    | "not_in_registry";

const ComparisonBlock = ({ comparison }: { comparison: VerificationComparison }) => {
  const { t } = useTranslation("kyb");
  const rows = [
    { key: "legalName", ...comparison.legalName },
    { key: "registrationNumber", ...comparison.registrationNumber },
  ] as const;
  return (
    <div className="kyb-sub">
      <h4 className="kyb-sub__title">{t("check.comparison.title")}</h4>
      <p className="kyb-note">{t("check.comparison.help")}</p>
      <div className="kyb-scroll">
        <table className="kyb-table">
          <thead>
            <tr>
              <th />
              <th>{t("check.comparison.declared")}</th>
              <th>{t("check.comparison.registry")}</th>
              <th>{t("check.comparison.match")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{t(`check.comparison.fields.${row.key}`)}</td>
                <td>{row.declared || EMPTY_VALUE}</td>
                <td>{row.registry || EMPTY_VALUE}</td>
                <td>
                  <span className={`mod-page__chip ${MATCH_CHIP[row.match] ?? ""}`}>
                    {t(`check.comparison.matchValue.${matchKey(row.match)}`)}
                  </span>
                </td>
              </tr>
            ))}
            {comparison.people.map((p) => (
              <tr key={`declared-${p.declaredName}`}>
                <td>{t("check.comparison.fields.shareholder")}</td>
                <td>
                  {p.declaredName}
                  {p.declaredOwnership ? <div className="kyb-note">{p.declaredOwnership}</div> : null}
                </td>
                <td>
                  {p.registryName ?? EMPTY_VALUE}
                  {p.registryRole || p.registryOwnership ? (
                    <div className="kyb-note">{[p.registryRole, p.registryOwnership].filter(Boolean).join(" · ")}</div>
                  ) : null}
                </td>
                <td>
                  <span className={`mod-page__chip ${MATCH_CHIP[p.match] ?? ""}`}>
                    {t(`check.comparison.matchValue.${matchKey(p.match)}`)}
                  </span>
                </td>
              </tr>
            ))}
            {comparison.registryOnlyPeople.map((p) => (
              <tr key={`registry-${p.name}-${p.role}`}>
                <td>{t("check.comparison.fields.undeclared")}</td>
                <td>{EMPTY_VALUE}</td>
                <td>
                  {p.name}
                  <div className="kyb-note">{[p.role, p.ownership].filter(Boolean).join(" · ")}</div>
                </td>
                <td>
                  <span className="mod-page__chip mod-page__chip--warning">{t("check.comparison.notDeclared")}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!comparison.registryHasPeople ? <p className="kyb-note">{t("check.comparison.noRegistryPeople")}</p> : null}
    </div>
  );
};

// ------------------------------------------------------------------------ Hits

const HitRow = ({ hit, canAct, onDecided }: { hit: VerificationHit; canAct: boolean; onDecided: () => void }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const decide = useVerificationModeratorHitsDecideCreate({
    mutation: {
      onSuccess: () => {
        setReason("");
        onDecided();
      },
    },
  });
  const act = (decision: "confirmed" | "dismissed") => {
    if (!reason.trim()) {
      setReasonError(true);
      return;
    }
    setReasonError(false);
    decide.mutate({ id: hit.id, data: { decision, reason: reason.trim() } });
  };
  const chip =
    hit.decision === "confirmed"
      ? "mod-page__chip--danger"
      : hit.decision === "dismissed"
        ? ""
        : "mod-page__chip--warning";

  return (
    <div className="kyb-badge-option">
      <div>
        <strong>{hit.matchedName}</strong>{" "}
        <span className="mod-page__chip">{t(`check.hitCategory.${hit.category}`)}</span>
        <span className="mod-page__chip">{t("check.hit.score", { score: hit.score })}</span>
        <span className={`mod-page__chip ${chip}`}>{t(`check.hitDecision.${hit.decision}`)}</span>
      </div>
      <p className="kyb-note">
        {t("check.hit.subject", { name: hit.subjectName, type: hit.subjectType })} ·{" "}
        {t("check.hit.list", { name: hit.listName })}
      </p>
      {hit.decidedBy && hit.decidedAt ? (
        <p className="kyb-note">
          {t("check.decidedBy", { name: hit.decidedBy, date: formatDate(hit.decidedAt) })}
          {hit.decisionReason ? ` — ${hit.decisionReason}` : ""}
        </p>
      ) : null}
      {canAct ? (
        <div className="kyb-form">
          <input
            className="mod-page__input"
            placeholder={t("check.hit.reasonPlaceholder")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {reasonError ? <div className="mod-page__inline-error">{t("check.hit.reasonRequired")}</div> : null}
          <div className="mod-page__form-row">
            <button
              type="button"
              className="mod-page__btn mod-page__btn--danger"
              disabled={decide.isPending}
              onClick={() => act("confirmed")}
            >
              {t("check.hit.confirm")}
            </button>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--ghost"
              disabled={decide.isPending}
              onClick={() => act("dismissed")}
            >
              {t("check.hit.dismiss")}
            </button>
          </div>
          {decide.isError ? (
            <div className="mod-page__inline-error">{getApiErrorMessage(decide.error, t("check.hit.decideError"))}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// -------------------------------------------------------------------- Proposal

const ProposalBlock = ({ check }: { check: VerificationCheck }) => {
  const { t } = useTranslation("kyb");
  const entries = Object.entries(check.proposal ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) return null;
  return (
    <div className="kyb-sub">
      <h4 className="kyb-sub__title">{t("check.proposal.title")}</h4>
      <p className="kyb-note">{t("check.proposal.help")}</p>
      <ul className="kyb-note">
        {entries.map(([field, value]) => (
          <li key={field}>
            {t(`field.${field as VerificationFieldEnum}`)}: <strong>{String(value)}</strong>
            {check.appliedFields.includes(field) ? ` ${t("check.proposal.applied")}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
};

// -------------------------------------------------------------------- Decision

const DecisionBlock = ({
  check,
  isSuper,
  onChanged,
  dismissOnly = false,
}: {
  check: VerificationCheck;
  isSuper: boolean;
  onChanged: () => void;
  dismissOnly?: boolean;
}) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [reason, setReason] = useState("");
  const confirm = useVerificationModeratorChecksConfirmCreate({ mutation: { onSuccess: onChanged } });
  const dismiss = useVerificationModeratorChecksDismissCreate({ mutation: { onSuccess: onChanged } });

  if (!isSuper) return <p className="kyb-note kyb-sub">{t("check.decide.superOnly")}</p>;

  const pendingHits = check.hits.filter((h) => h.decision === "pending").length;
  const outcome = confirmOutcome(check);

  return (
    <div className="kyb-sub kyb-form">
      <h4 className="kyb-sub__title">{t("check.decide.title")}</h4>
      {!dismissOnly ? (
        pendingHits > 0 ? (
          <div className="kyb-callout kyb-callout--warning">
            {t("check.decide.pendingHits", { count: pendingHits })}
          </div>
        ) : (
          <div className={`kyb-callout ${outcome === "approved" ? "kyb-callout--success" : "kyb-callout--danger"}`}>
            {t(`check.decide.preview.${outcome}`)}
          </div>
        )
      ) : (
        <p className="kyb-note">{t("check.decide.dismissHelp")}</p>
      )}
      <textarea
        className="mod-page__input"
        rows={2}
        placeholder={t("check.decide.reasonPlaceholder")}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="mod-page__form-row">
        {!dismissOnly ? (
          <button
            type="button"
            className="mod-page__btn mod-page__btn--primary"
            disabled={pendingHits > 0 || confirm.isPending}
            onClick={() => confirm.mutate({ id: check.id, data: { reason: reason.trim() || undefined } })}
          >
            {confirm.isPending ? t("check.decide.confirming") : t("check.decide.confirm")}
          </button>
        ) : null}
        <button
          type="button"
          className="mod-page__btn mod-page__btn--danger"
          disabled={dismiss.isPending}
          onClick={() => dismiss.mutate({ id: check.id, data: { reason: reason.trim() || undefined } })}
        >
          {dismiss.isPending ? t("check.decide.dismissing") : t("check.decide.dismiss")}
        </button>
      </div>
      {confirm.isError ? (
        <div className="mod-page__inline-error">
          {getApiErrorMessage(confirm.error, t("check.decide.confirmError"))}
        </div>
      ) : null}
      {dismiss.isError ? (
        <div className="mod-page__inline-error">
          {getApiErrorMessage(dismiss.error, t("check.decide.dismissError"))}
        </div>
      ) : null}
    </div>
  );
};
